/* oxlint-disable react/static-components -- these render APIs intentionally receive component references */
import moment from 'moment'
import { useEffect, useState } from 'react'
import { LAST_COMM_CALL_QUERY } from './graphql'
import { contactLastComm, LAST_COMM_CHANNELS, lastCommIcon, lastCommLabel } from 'lib/lastCommMedium'
import type { Contact } from 'app/generated/graphql'
import { CallDetails, LastCommTooltip } from './LazyLastCommPanel'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import { bg } from 'lib/background'
import { run } from 'lib/run'
import { createBatchLoader } from 'app/lib/batchLoader'
import { gqlFetchList, gqlQuery } from 'app/lib/graphqlClient'

interface FollowUpDetails {
  isConnected?: boolean
  isScheduled?: boolean
  appointmentDate?: Date | string
  description?: string
}

interface FormDetails {
  patientName?: string
  email?: string
  phone?: string
  title?: string
}

interface SchedulerDetails {
  appointmentDate?: Date | string
  appointmentType?: string
}

interface LastCommData {
  displayText: string
  activityType: string
  activityDate: Date | string
  /** Id of the linked record (a Call, for call activities). */
  typeId?: string
  followUp?: FollowUpDetails
  summary?: string
  form?: FormDetails
  scheduler?: SchedulerDetails
}

let fetching: Record<string, Promise<LastCommData | null>> = {}
let cache: Record<string, LastCommData | null> = {}
/**
 * The `lastCommDate` each cached entry was resolved against.
 *
 * Refresh the contact-keyed enrichment caches when the stored date changes.
 * The visible label and timestamp come directly from the contact descriptor.
 */
const loadedFor: Record<string, string> = {}

function formatRelativeDate(date: Date | string): string {
  const activityDate = moment.utc(date).startOf(`day`)
  const today = moment().startOf(`day`)
  const yesterday = moment().subtract(1, `day`).startOf(`day`)

  if (activityDate.isSame(today, `day`)) {
    return `Today`
  } else if (activityDate.isSame(yesterday, `day`)) {
    return `Yesterday`
  } else {
    const daysAgo = today.diff(activityDate, `days`)
    return `${daysAgo} Days Ago`
  }
}

const COM_ACTIVITY_TYPES = [
  `Call`,
  `Form`,
  `Imported Form`,
  `Chat`,
  `Online Scheduler`,
  `Virtual Consultation`,
  `Scheduler`,
  `FollowUp`,
]

type LastCommActivity = {
  id: string
  type: string
  activityDate: string
  description?: string
  data?: Record<string, unknown>
  typeId?: string
  contacts?: { id: string }[]
}

/** Shapes one raw activity into the cell/panel's view model. */
function toLastCommData(activity: LastCommActivity): LastCommData {
  const activityType = activity.type
  let followUp: FollowUpDetails | undefined
  let summary: string | undefined
  let form: FormDetails | undefined
  let scheduler: SchedulerDetails | undefined

  if (activityType === `FollowUp` && activity.data) {
    followUp = {
      isConnected: activity.data.isConnected as boolean | undefined,
      isScheduled: activity.data.isScheduled as boolean | undefined,
      appointmentDate: activity.data.appointmentDate as string | undefined,
      description: activity.description,
    }
  } else if (activityType === `Form` || activityType === `Imported Form`) {
    const data = activity.data ?? {}
    form = {
      patientName: data.patientName as string | undefined,
      email: data.email as string | undefined,
      phone: data.phone as string | undefined,
      title: data.title as string | undefined,
    }
  } else if (activityType === `Scheduler` || activityType === `Online Scheduler`) {
    const data = activity.data ?? {}
    scheduler = {
      appointmentDate: data.appointmentDate as string | undefined,
      appointmentType: data.appointmentType as string | undefined,
    }
  } else {
    summary = activity.description || undefined
  }

  return {
    displayText: formatRelativeDate(activity.activityDate),
    activityType,
    activityDate: activity.activityDate,
    typeId: activity.typeId,
    followUp,
    summary,
    form,
    scheduler,
  }
}

/**
 * Batches activity enrichment for visible contacts. The first hit per contact
 * wins because results are newest-first. A busy contact can crowd other contacts
 * out of this window; those contacts use the per-contact enrichment fetch.
 * These activities never determine the visible communication descriptor.
 */
const lastCommBatchLoader = createBatchLoader<string, LastCommData | null>(async (contactIds) => {
  const uniqueIds = [...new Set(contactIds)]
  const typeFilter = COM_ACTIVITY_TYPES.map((t) => `"${t}"`).join(` OR `)
  const idFilter = uniqueIds.map((id) => `"${id}"`).join(` OR `)
  const result = await gqlFetchList<LastCommActivity>(
    `activities`,
    {
      search: `contacts.id.keyword:(${idFilter}) type:(${typeFilter})`,
      sort: { field: `activityDate`, direction: `DESC` },
      pagination: { limit: Math.min(uniqueIds.length * 10, 500) },
    },
    `id type activityDate description data typeId contacts { id }`,
  )

  const map = new Map<string, LastCommData | null>()
  for (const activity of result.hits) {
    for (const contact of activity.contacts ?? []) {
      // Newest-first: keep the first activity seen per contact.
      if (contact?.id && uniqueIds.includes(contact.id) && !map.has(contact.id)) {
        map.set(contact.id, toLastCommData(activity))
      }
    }
  }
  for (const id of uniqueIds) {
    const value = map.get(id) ?? null
    map.set(id, value)
    cache[id] = value
  }
  return map
})

/** Forget what we know about one contact's last communication. */
export function invalidateLastComm(contactId: string) {
  delete cache[contactId]
  delete fetching[contactId]
  delete loadedFor[contactId]
  lastCommBatchLoader.invalidate(contactId)
}

export async function getLastCommActivity(contactId: string): Promise<LastCommData | null> {
  if (cache[contactId] != null) return cache[contactId]
  if (fetching[contactId] != null) return fetching[contactId]
  else if (!contactId || contactId === `undefined`) return null

  return (fetching[contactId] = run(async () => {
    const typeFilter = COM_ACTIVITY_TYPES.map((t) => `"${t}"`).join(` OR `)
    const result = await gqlFetchList<{
      id: string
      type: string
      activityDate: string
      description?: string
      data?: Record<string, unknown>
      typeId?: string
    }>(
      `activities`,
      {
        search: `contacts.id.keyword:${contactId} type:(${typeFilter})`,
        sort: { field: `activityDate`, direction: `DESC` },
        pagination: { limit: 1 },
      },
      `id type activityDate description data typeId`,
    )

    const activity = result.hits[0]
    if (!activity) {
      return (cache[contactId] = null)
    }

    const displayText = formatRelativeDate(activity.activityDate)
    const activityType = activity.type

    let followUp: FollowUpDetails | undefined
    let summary: string | undefined
    let form: FormDetails | undefined
    let scheduler: SchedulerDetails | undefined

    if (activityType === `FollowUp` && activity.data) {
      followUp = {
        isConnected: activity.data.isConnected as boolean | undefined,
        isScheduled: activity.data.isScheduled as boolean | undefined,
        appointmentDate: activity.data.appointmentDate as string | undefined,
        description: activity.description,
      }
    } else if (activityType === `Call`) {
      summary = activity.description || undefined
    } else if (activityType === `Form` || activityType === `Imported Form`) {
      const data = activity.data ?? {}
      form = {
        patientName: data.patientName as string | undefined,
        email: data.email as string | undefined,
        phone: data.phone as string | undefined,
        title: data.title as string | undefined,
      }
    } else if (activityType === `Scheduler` || activityType === `Online Scheduler`) {
      const data = activity.data ?? {}
      scheduler = {
        appointmentDate: data.appointmentDate as string | undefined,
        appointmentType: data.appointmentType as string | undefined,
      }
    } else {
      summary = activity.description || undefined
    }

    return (cache[contactId] = {
      displayText,
      activityType,
      activityDate: activity.activityDate,
      typeId: activity.typeId,
      followUp,
      summary,
      form,
      scheduler,
    })
  }))
}

type CallQueryResult = {
  call?: {
    direction?: string
    duration?: number
    answered?: boolean
    answerer?: string
    note?: string
    allPatientOutcomes?: string[]
    ai?: { summary?: string; answerer?: string }
  }
}

const callCache: Record<string, CallDetails | null> = {}
const callFetching: Record<string, Promise<CallDetails | null>> = {}

/**
 * A call activity's own description is just its direction label, so the useful
 * detail (the AI summary, who took it, what it led to) lives on the linked Call.
 * Fetched on hover only, and once per call for the session.
 */
async function getCallDetails(callId: string): Promise<CallDetails | null> {
  if (callId in callCache) return callCache[callId]
  if (callId in callFetching) return callFetching[callId]

  return (callFetching[callId] = run(async () => {
    const result = await gqlQuery<CallQueryResult>(LAST_COMM_CALL_QUERY, { id: callId })
    const call = result.data?.call
    if (!call) return (callCache[callId] = null)
    return (callCache[callId] = {
      direction: call.direction ?? undefined,
      duration: call.duration ?? undefined,
      answered: call.answered ?? undefined,
      answerer: call.answerer ?? call.ai?.answerer ?? undefined,
      summary: call.ai?.summary ?? call.note ?? undefined,
      outcomes: call.allPatientOutcomes ?? undefined,
    })
  }))
}

export function LazyLastCommCell({
  contactId,
  lastCommDate,
  lastCommActivityType,
  lastCommActivitySubType,
  className,
  wrapperClassName,
}: Pick<Contact, `lastCommDate` | `lastCommActivityType` | `lastCommActivitySubType`> & {
  contactId?: string
  /** Type-label typography. The date beside it stays quiet either way. */
  className?: string
  /**
   * Replaces the layout the table row needs, for callers that are not a table
   * row. The default carries `mx-2` and `h-full`, which are a cell's padding
   * and a cell's height; on an Action Center card both are wrong.
   */
  wrapperClassName?: string
}) {
  const communication = contactLastComm({ lastCommDate, lastCommActivityType, lastCommActivitySubType })
  const [loadedActivity, setLoadedActivity] = useState<{ contactId: string; data: LastCommData | null } | null>(null)
  const [loadedCall, setLoadedCall] = useState<{ id: string; details: CallDetails | null } | null>(null)
  const [loadingCallId, setLoadingCallId] = useState<string | null>(null)
  // Enrich only when the activity agrees with the stored channel AND timestamp.
  // A text's date must never acquire an older call's tooltip, even during a row update.
  const candidate = loadedActivity && loadedActivity.contactId === contactId ? loadedActivity.data : null
  const commData =
    candidate &&
    communication?.type &&
    LAST_COMM_CHANNELS.get(candidate.activityType) === communication.type &&
    new Date(candidate.activityDate).getTime() === new Date(communication.date).getTime()
      ? candidate
      : null
  const callId = commData?.activityType === `Call` ? commData.typeId : undefined
  const callDetails = callId && loadedCall?.id === callId ? loadedCall.details : null
  const channel = communication?.type

  // The stored fields render immediately. Activity queries only enrich hover details.
  useEffect(() => {
    if (!contactId || !lastCommDate || !channel || channel === `text` || channel === `email`) return
    // Refresh enrichment when the stored date changes.
    const stamp = String(lastCommDate)
    if (loadedFor[contactId] && loadedFor[contactId] !== stamp) {
      invalidateLastComm(contactId)
      setLoadedActivity(null)
    }
    loadedFor[contactId] = stamp
    let cancelled = false
    bg(
      lastCommBatchLoader.load(contactId).then(async (data) => {
        if (cancelled) return
        if (data) {
          setLoadedActivity({ contactId, data })
          return
        }
        // A busy contact can crowd another contact's enrichment out of the batch.
        const single = await getLastCommActivity(contactId)
        if (cancelled) return
        setLoadedActivity({ contactId, data: single })
      }),
    )
    return () => {
      cancelled = true
    }
  }, [contactId, lastCommDate, channel])

  const handleHover = () => {
    if (!callId) return
    if (callId in callCache) {
      setLoadedCall({ id: callId, details: callCache[callId] })
      return
    }
    setLoadingCallId(callId)
    bg(
      getCallDetails(callId).then((details) => {
        // Key the result to the call so an old request cannot enrich a newer row.
        setLoadedCall({ id: callId, details })
        setLoadingCallId((current) => (current === callId ? null : current))
      }),
    )
  }

  if (!contactId || !lastCommDate) return null

  const displayText = formatRelativeDate(lastCommDate)
  const typeLabel = communication?.type ? lastCommLabel(communication) : undefined
  const Icon = lastCommIcon(communication?.type)

  return (
    <TooltipWrapper
      placement="top-start"
      delayDuration={300}
      onMouseOver={handleHover}
      // The row is clickable; TooltipWrapper's default cursor-default would say otherwise.
      className={wrapperClassName ?? `mx-2 flex h-full min-w-0 cursor-[inherit] items-center gap-1.5 align-middle`}
      // Important: the global .tooltip-wrapper class hard-sets p-2, which is right
      // for a one-line tooltip and cramped for a four-line card.
      tooltipClassName="max-w-80 !px-3.5 !py-2.5"
      message={
        <LastCommTooltip
          communication={communication}
          activityType={commData?.activityType}
          activityDate={lastCommDate}
          followUp={commData?.followUp}
          summary={commData?.summary}
          form={commData?.form}
          scheduler={commData?.scheduler}
          call={callDetails ?? undefined}
          loadingCall={Boolean(callId && loadingCallId === callId)}
        />
      }
    >
      <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center">
        {Icon && <Icon className="h-3.5 w-3.5 animate-fade text-minimal animate-duration-200" strokeWidth={2} />}
      </span>
      <div className="min-w-0 animate-fade overflow-hidden overflow-ellipsis whitespace-nowrap animate-duration-200">
        {typeLabel && <span className={className ?? `text-body-xs`}>{typeLabel}</span>}
        {typeLabel && <span className="mx-1 text-minimal">·</span>}
        <span className={typeLabel ? `text-body-xs text-subtle` : (className ?? `text-body-xs`)}>{displayText}</span>
      </div>
    </TooltipWrapper>
  )
}
